/**
 * Keeps member table structure and body states independent of query and modal state.
 */
import React from 'react';
import Alert from '@amzn/meridian/alert';
import DataTable, {
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
} from '@amzn/meridian/data-table';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import type { BusinessUnitMemberDto } from 'src/store/generated/leela-api-generated';
import { MemberTableRow } from './MemberTableRow';

interface MembersTableProps {
  isPageTransitionError: boolean;
  isPageTransitionLoading: boolean;
  members: BusinessUnitMemberDto[];
  onEdit: (member: BusinessUnitMemberDto) => void;
}

export const MembersTable: React.FC<MembersTableProps> = ({
  isPageTransitionError,
  isPageTransitionLoading,
  members,
  onEdit,
}) => {
  const { t } = useTranslation();
  const shouldRenderMembers =
    !isPageTransitionError && !isPageTransitionLoading;

  return (
    <DataTable
      data-testid="access-members-table"
      showBorder
      showRowDividers
      width="100%"
    >
      <DataTableHead backgroundColor="secondary">
        <DataTableRow>
          <DataTableHeader>
            {t('access_management_table_user')}
          </DataTableHeader>
          <DataTableHeader width={100}>
            {t('access_management_table_type')}
          </DataTableHeader>
          <DataTableHeader>
            {t('access_management_table_groups')}
          </DataTableHeader>
          <DataTableHeader width={100}>
            {t('access_management_table_actions')}
          </DataTableHeader>
        </DataTableRow>
      </DataTableHead>
      <DataTableBody>
        {isPageTransitionError && (
          <DataTableRow>
            <DataTableCell colSpan={4}>
              <Alert type="error">{t('access_management_load_error')}</Alert>
            </DataTableCell>
          </DataTableRow>
        )}
        {!isPageTransitionError && isPageTransitionLoading && (
          <DataTableRow>
            <DataTableCell colSpan={4}>
              <Row alignmentHorizontal="center">
                <Loader />
              </Row>
            </DataTableCell>
          </DataTableRow>
        )}
        {shouldRenderMembers && members.length === 0 && (
          <DataTableRow>
            <DataTableCell colSpan={4}>
              <Text color="secondary">
                {t('access_management_no_members')}
              </Text>
            </DataTableCell>
          </DataTableRow>
        )}
        {shouldRenderMembers && members.map((member) => (
          <MemberTableRow
            key={member.userId}
            member={member}
            onEdit={onEdit}
          />
        ))}
      </DataTableBody>
    </DataTable>
  );
};
